import { useEffect, useRef, useState } from "react";
import { peso, fmtElapsed, STATUS_META } from "../lib/api";

export const cx = (...a) => a.filter(Boolean).join(" ");

export function useInterval(callback, ms) {
  const ref = useRef(callback);
  useEffect(() => { ref.current = callback; });
  useEffect(() => {
    const id = setInterval(() => ref.current && ref.current(), ms);
    return () => clearInterval(id);
  }, [ms]);
}

export function Card({ className, children, ...rest }) {
  return (
    <div className={cx("bg-[#1C2541] border border-cyan-500/15 rounded-2xl p-4 shadow-lg", className)} {...rest}>
      {children}
    </div>
  );
}

export function Kpi({ label, value, sub, testid, accent = "text-cyan-400" }) {
  return (
    <div data-testid={testid} className="bg-[#1C2541]/80 backdrop-blur-md border border-cyan-500/20 p-4 rounded-2xl relative overflow-hidden">
      <div className="text-[0.65rem] font-bold tracking-widest uppercase text-slate-400">{label}</div>
      <div className={cx("mono text-2xl sm:text-3xl font-extrabold tracking-tight mt-1", accent)}>{value}</div>
      {sub && <div className="text-xs text-slate-400 mt-0.5">{sub}</div>}
    </div>
  );
}

export function StatusBadge({ status, testid }) {
  const m = STATUS_META[status] || { label: status, cls: "bg-slate-500/10 text-slate-400 border-slate-500/30" };
  return (
    <span data-testid={testid} className={cx("inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border", m.cls)}>
      {m.label}
    </span>
  );
}

export function LiveTimer({ from, to, targetMin, testid }) {
  const [, setT] = useState(0);
  useInterval(() => !to && setT((x) => x + 1), 1000);
  const txt = fmtElapsed(from, to);
  let warn = false;
  if (targetMin && from && !to) {
    warn = (Date.now() - new Date(from).getTime()) / 60000 > targetMin;
  }
  return (
    <span data-testid={testid} className={cx(
      "mono text-sm tracking-widest px-2.5 py-1 rounded-lg border inline-flex items-center gap-1.5",
      warn ? "bg-orange-950/60 text-orange-400 border-orange-500/40" : "bg-cyan-950/70 text-cyan-400 border-cyan-500/30"
    )}>
      ⏱ {txt}
    </span>
  );
}

export function Money({ value, className }) {
  return <span className={cx("mono", className)}>{peso(value)}</span>;
}

export function Btn({ variant = "primary", className, children, ...rest }) {
  const styles = {
    primary: "bg-cyan-500 hover:bg-cyan-400 text-slate-950 shadow-[0_0_15px_rgba(0,210,254,0.3)]",
    ghost: "bg-[#0B132B] hover:bg-[#222D4F] text-slate-200 border border-slate-700",
    danger: "bg-rose-600 hover:bg-rose-500 text-white",
    success: "bg-emerald-500 hover:bg-emerald-400 text-slate-950",
    warn: "bg-amber-500 hover:bg-amber-400 text-slate-950",
  }[variant];
  return (
    <button className={cx("font-bold px-4 py-2.5 rounded-xl transition-all active:scale-95 disabled:opacity-40 disabled:pointer-events-none min-h-[44px]", styles, className)} {...rest}>
      {children}
    </button>
  );
}

export function Field({ label, testid, ...rest }) {
  return (
    <label className="block">
      {label && <span className="ccc-label">{label}</span>}
      <input className="ccc-input" data-testid={testid} {...rest} />
    </label>
  );
}

export function Empty({ children }) {
  return <div className="text-center text-slate-500 py-10 text-sm">{children}</div>;
}
